import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { clientService } from '../services/clientService'
import { Client } from '../types'
import {
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  EnvelopeIcon,
  PhoneIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  UserIcon
} from '@heroicons/react/24/outline'
import AppHeader from '../components/AppHeader'

const ClientsPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  
  const [clients, setClients] = useState<Client[]>([])
  const [filteredClients, setFilteredClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedClient, setSelectedClient] = useState<Client | null>(null)
  const [showClientModal, setShowClientModal] = useState(false)

  useEffect(() => {
    if (user?.id) {
      loadClients()
    }
  }, [user?.id])

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredClients(clients)
    } else {
      const filtered = clients.filter(client =>
        client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (client.company && client.company.toLowerCase().includes(searchQuery.toLowerCase()))
      )
      setFilteredClients(filtered)
    }
  }, [searchQuery, clients])

  const loadClients = async () => {
    try {
      setLoading(true)
      const clientsData = await clientService.getClientsForUser(user!.id)
      setClients(clientsData)
      setFilteredClients(clientsData)
    } catch (err) {
      console.error('Error loading clients:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteClient = async (clientId: string) => {
    if (window.confirm('Are you sure you want to delete this client? This action cannot be undone.')) {
      try {
        await clientService.deleteClient(clientId)
        setClients(prev => prev.filter(client => client.id !== clientId))
        setShowClientModal(false)
      } catch (err) {
        console.error('Error deleting client:', err)
        alert('Failed to delete client. Please try again.')
      }
    }
  }

  const parseClientNotes = (notes: string) => {
    const lines = notes.split('\n')
    const parsedData: any = {}
    const regularNotes: string[] = []

    lines.forEach(line => {
      if (line.includes(':')) {
        const [key, value] = line.split(':').map(s => s.trim())
        const lowerKey = key.toLowerCase()
        if (['address', 'city', 'state', 'zip', 'country', 'website', 'instagram', 'referral', 'project type', 'budget'].includes(lowerKey)) {
          parsedData[lowerKey] = value
        } else {
          regularNotes.push(line)
        }
      } else if (line.trim()) {
        regularNotes.push(line)
      }
    })

    return {
      ...parsedData,
      notes: regularNotes.join('\n').trim()
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="floating-orb floating-orb-1"></div>
          <div className="floating-orb floating-orb-2"></div>
          <div className="floating-orb floating-orb-3"></div>
        </div>
        <div className="glass-mockup rounded-3xl p-8">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-white mt-4">Loading clients...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <AppHeader 
        title={`Clients (${filteredClients.length})`}
      >
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/dashboard')}
          className="flex items-center space-x-3 text-white hover:text-gray-300 transition-colors"
        >
          <ArrowLeftIcon className="w-6 h-6" />
          <span className="text-lg font-medium">Back to Dashboard</span>
        </motion.button>
      </AppHeader>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Add Client */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-mockup rounded-2xl p-6 mb-8"
        >
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search clients by name, email, or company..."
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
              />
            </div>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              onClick={() => navigate('/add-client')}
              className="flex items-center space-x-2 px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-medium hover:from-emerald-600 hover:to-teal-700 transition-all"
            >
              <PlusIcon className="w-5 h-5" />
              <span>Add Client</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Clients Grid */}
        {filteredClients.length > 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredClients.map((client, index) => (
              <motion.div
                key={client.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.05 }}
                className="glass-mockup rounded-2xl p-6 hover:scale-105 transition-all group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                      {client.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">{client.name}</h3>
                      {client.company && (
                        <p className="text-gray-400 text-sm">{client.company}</p>
                      )}
                    </div>
                  </div>
                  
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    onClick={() => {
                      setSelectedClient(client)
                      setShowClientModal(true)
                    }}
                    className="opacity-0 group-hover:opacity-100 p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all"
                  >
                    <EyeIcon className="w-4 h-4" />
                  </motion.button>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-gray-400 text-sm">
                    <EnvelopeIcon className="w-4 h-4" />
                    <span className="truncate">{client.email}</span>
                  </div>
                  
                  {client.phone && (
                    <div className="flex items-center space-x-2 text-gray-400 text-sm">
                      <PhoneIcon className="w-4 h-4" />
                      <span>{client.phone}</span>
                    </div>
                  )}
                </div>
                
                <div className="mt-4 pt-4 border-t border-white/10">
                  <p className="text-gray-500 text-xs">
                    Added {new Date(client.created_at).toLocaleDateString()}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-mockup rounded-2xl p-12 text-center"
          >
            <UserIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              {searchQuery ? 'No clients found' : 'No clients yet'}
            </h3>
            <p className="text-gray-400 mb-6">
              {searchQuery 
                ? 'Try adjusting your search terms'
                : 'Start building your client database by adding your first client'
              }
            </p>
            {!searchQuery && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                onClick={() => navigate('/add-client')}
                className="px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-medium hover:from-emerald-600 hover:to-teal-700 transition-all"
              >
                Add Your First Client
              </motion.button>
            )}
          </motion.div>
        )}
      </main>

      {/* Client Detail Modal */}
      {showClientModal && selectedClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowClientModal(false)} />
          
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="relative glass-mockup rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Client Details</h2>
              <div className="flex items-center space-x-2">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  className="p-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 transition-all"
                >
                  <PencilIcon className="w-5 h-5" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  onClick={() => handleDeleteClient(selectedClient.id)}
                  className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-all"
                >
                  <TrashIcon className="w-5 h-5" />
                </motion.button>
                <button
                  onClick={() => setShowClientModal(false)}
                  className="text-gray-400 hover:text-white transition-colors text-2xl"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="p-6 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                    {selectedClient.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">{selectedClient.name}</h3>
                    {selectedClient.company && (
                      <p className="text-gray-400">{selectedClient.company}</p>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <EnvelopeIcon className="w-5 h-5 text-gray-400" />
                    <span className="text-white">{selectedClient.email}</span>
                  </div>
                  
                  {selectedClient.phone && (
                    <div className="flex items-center space-x-3">
                      <PhoneIcon className="w-5 h-5 text-gray-400" />
                      <span className="text-white">{selectedClient.phone}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Additional Information */}
              {selectedClient.notes && (() => {
                const parsedData = parseClientNotes(selectedClient.notes)
                return (
                  <div className="space-y-4">
                    {(parsedData.address || parsedData.city || parsedData.state) && (
                      <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                        <h4 className="text-white font-medium mb-3">Address</h4>
                        <div className="text-gray-400 space-y-1">
                          {parsedData.address && <p>{parsedData.address}</p>}
                          {(parsedData.city || parsedData.state) && (
                            <p>{[parsedData.city, parsedData.state].filter(Boolean).join(', ')}</p>
                          )}
                          {parsedData.country && <p>{parsedData.country}</p>}
                        </div>
                      </div>
                    )}
                    
                    {(parsedData.website || parsedData.instagram) && (
                      <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                        <h4 className="text-white font-medium mb-3">Online Presence</h4>
                        <div className="space-y-2">
                          {parsedData.website && (
                            <p className="text-gray-400">Website: {parsedData.website}</p>
                          )}
                          {parsedData.instagram && (
                            <p className="text-gray-400">Instagram: {parsedData.instagram}</p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {(parsedData['project type'] || parsedData.budget || parsedData.referral) && (
                      <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                        <h4 className="text-white font-medium mb-3">Project Information</h4>
                        <div className="space-y-2">
                          {parsedData['project type'] && (
                            <p className="text-gray-400">Project Type: {parsedData['project type']}</p>
                          )}
                          {parsedData.budget && (
                            <p className="text-gray-400">Budget: {parsedData.budget}</p>
                          )}
                          {parsedData.referral && (
                            <p className="text-gray-400">Referral Source: {parsedData.referral}</p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {parsedData.notes && (
                      <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                        <h4 className="text-white font-medium mb-3">Notes</h4>
                        <p className="text-gray-400 whitespace-pre-wrap">{parsedData.notes}</p>
                      </div>
                    )}
                  </div>
                )
              })()}
              
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <h4 className="text-white font-medium mb-3">Account Information</h4>
                <div className="space-y-2 text-gray-400 text-sm">
                  <p>Created: {new Date(selectedClient.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</p>
                  <p>Last Updated: {new Date(selectedClient.updated_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default ClientsPage 